import clsx from 'clsx'
import first from 'lodash/first'
import isEqual from 'lodash/isEqual'
import map from 'lodash/map'
import noop from 'lodash/noop'
import React, { FC, useContext, useEffect, useMemo, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import SvgAlert from '@Assets/SVG/Alert'
import PaymentHeaderIcon from '@Assets/SVG/Payment'
import { Button } from '@CoreComponents/Buttons/Button'
import { ShippingLogo } from '@Domains/Checkout/Steps/Delivery/Components/DeliveryLogo'
import { PaymentIcon } from '@Domains/Checkout/Steps/Payment/Components/PaymentIconFactory/PaymentIconFactory'
import { PaymentProviderBusProvider } from '@Domains/Checkout/Steps/Payment/PaymentProviderFactory/PaymentProviderBus'
import { CheckoutFlowContext } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/CheckoutFlowContext'
import { CheckoutFlowStep, CheckoutFlowSteps } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/typings/CheckoutFlowTypes'
import { COD_PAYMENT, FREE_PAYMENT } from '@Domains/CheckoutV2/Features/CheckoutSteps/Hooks/UseCheckoutFlowStepValidation'
import {
  CheckoutPaymentOption,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/CheckoutPaymentOption/CheckoutPaymentOption'
import {
  CheckoutTermsOfService,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/CheckoutTermsOfService/CheckoutTermsOfService'
import { CheckoutTotals } from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/CheckoutTotals/CheckoutTotals'
import { usePaymentComponentFactory } from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Hooks/usePaymentComponentFactory'
import { MetadataContext } from '@Globals/Metadata'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'
import { CheckoutV2PaymentMethod } from '@Pages/onestepcheckout'
import { useDefaultCheckoutResponse } from '@PaymentComponents/Default/hooks/useDefaultCheckoutResponse'
import { useCheckboxActionRef } from '@Shared/Components/Inputs/Checkbox/Checkbox'
import { CollapsableSection } from '@Shared/Components/Sections/CollapsableSection/CollapsableSection'

import styles from './CheckoutPaymentStep.module.scss'

export interface CheckoutPaymentStepProps {
  onErrors?: (errors: string[]) => void
}

export const CheckoutPaymentStep: FC<CheckoutPaymentStepProps> = () => {
  const cofDesignV1 = useSplitTest('cof_design_v1', 'b')
  const { store } = useContext(MetadataContext)
  const { t } = useTranslation()
  const onlyBraintree = ['27', '36'].includes(store.id)
  const termsRef = useCheckboxActionRef()
  const [isApplePaySupported, setIsApplePaySupported] = useState<boolean>(false)
  const [paymentBeforeCOD, setPaymentBeforeCOD] = useState<CheckoutV2PaymentMethod | undefined>(undefined)
  const {
    data: {
      cart: {
        availablePaymentMethods,
        availableShippingMethods,
        shippingMethodID,
        totals,
      },
    },
    failedNavigation,
    failedNavigationAttempts,
    model: { paymentStep },
    setActiveStep,
    shippingLoading,
    submitCheckout,
    updateStep,
  } = useContext(CheckoutFlowContext)
  const selectedShippingMethod = useMemo(() => availableShippingMethods?.find(a => a.id === shippingMethodID), [availableShippingMethods, shippingMethodID])
  const grandTotal = (totals || []).find(total => total.id === 'grand_total')
  const {
    getButtonComponents,
    getFormComponents,
  } = usePaymentComponentFactory(grandTotal, shippingLoading)
  const activeButton = useMemo(() => {
    if (!paymentStep.payment || !grandTotal) {
      return null
    }
    return getButtonComponents(paymentStep.payment, submitCheckout, grandTotal)
  }, [getButtonComponents, grandTotal, paymentStep.payment, submitCheckout])
  const activeForm = useMemo(() => {
    if (!paymentStep.payment || !grandTotal) {
      return null
    }
    return getFormComponents(paymentStep.payment, submitCheckout, grandTotal)
  }, [getFormComponents, grandTotal, paymentStep.payment, submitCheckout])
  const [errorMessage, setErrorMessage] = useState<null | string>(null)
  const { handlePaymentSubmitResponse } = useDefaultCheckoutResponse()

  const isFree = useMemo(
    () => totals?.find(total => total.id === 'grand_total')?.value?.value === 0 || totals?.find(total => total.id === 'grand_total')?.value === null,
    [totals],
  )

  useEffect(() => {
    if (onlyBraintree) {
      if (typeof window !== 'undefined' && 'ApplePaySession' in window) {
        setIsApplePaySupported((window.ApplePaySession as any).canMakePayments())
      } else {
        setIsApplePaySupported(false)
      }
    } else {
      setIsApplePaySupported(true)
    }
  }, [onlyBraintree])

  useEffect(() => {
    if (failedNavigationAttempts && failedNavigationAttempts > 0 && failedNavigation === CheckoutFlowStep.SHIPPING) {
      termsRef.current?.validate()
      if (!paymentStep?.payment?.id) {
        setErrorMessage(t('checkout:COF_FILL_OUT_PAYMENT_INFO'))
        document.getElementById('checkout-payment-section')
          ?.scrollIntoView({ behavior: 'smooth' })
        return
      }
      if (!paymentStep?.terms) {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: 'smooth',
        })
        return
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failedNavigationAttempts])

  const hiddenOptions = useMemo(() => {
    const exclude = ['unknown', 'gift_card']
    if (!isApplePaySupported) {
      exclude.push('applepay')
    }
    return exclude
  }, [isApplePaySupported])

  const isCODSelected = useMemo(
    () => availableShippingMethods?.find(option => option.id === selectedShippingMethod?.id)?.isCOD,
    [availableShippingMethods, selectedShippingMethod?.id],
  )
  useEffect(() => {
    if (isCODSelected) {
      setPaymentBeforeCOD(paymentStep.payment)
      updateStep('paymentStep', {
        payment: undefined,
        terms: paymentStep.terms,
      }, true)
    } else if (!isCODSelected && !!paymentBeforeCOD) {
      updateStep('paymentStep', {
        payment: paymentBeforeCOD,
        terms: paymentStep.terms,
      }, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCODSelected])

  return (
    <div className={clsx(styles.checkoutPaymentStep, cofDesignV1 && styles.cofDesignV1)}>
      <PaymentProviderBusProvider>
        <CollapsableSection
          canCollapse={false}
          className={styles.paymentCollapseSection}
          hideSpinner
          icon={!cofDesignV1 && <PaymentHeaderIcon />}
          id="checkout-payment-section"
          isLoading={shippingLoading}
          title={cofDesignV1 ? t('checkout:COF_SELECT_A_PAYMENT_METHOD') : t('checkout:COF_PAYMENT')}
        >
          {errorMessage && (
            <p className={styles.error}>
              <SvgAlert />
              {errorMessage}
            </p>
          )}
          {!cofDesignV1 && !isFree && <h3>{t('checkout:COF_SELECT_A_PAYMENT_METHOD')}</h3>}
          <div className={styles.options}>
            {!isFree && !selectedShippingMethod?.isCOD
              && map(availablePaymentMethods, option => (
                <CheckoutPaymentOption
                  key={`payment_${option.id}_${option.type}`}
                  disabled={!!selectedShippingMethod?.isCOD}
                  hide={hiddenOptions.includes(option.type) || (onlyBraintree ? option.provider !== 'braintree' : false)}
                  icon={(<PaymentIcon className={styles[option.type]} name={option.type} url={option.logo?.url || ''} />)}
                  isSelected={!isCODSelected && isEqual(option.id, paymentStep?.payment?.id)}
                  onClick={() => {
                    updateStep('paymentStep', {
                      payment: { ...option },
                      terms: paymentStep.terms,
                    }, true)
                    ampli.cofPaymentOptionSelect({
                      // @ts-ignore
                      payment_method_type: option.type || '',
                      payment_option_id: option.id || '',
                      payment_provider: option.provider || '',
                    })
                  }}
                  title={option.title || first(option.icons)?.name || ''}
                />
              ))}
            {!isFree && map((availableShippingMethods || []).filter(option => option.isCOD && option.title && option.id && option.icon), option => (
              <CheckoutPaymentOption
                key={`payment_${option.id}`}
                disabled={selectedShippingMethod?.id !== option.id}
                icon={(<ShippingLogo deliveryType={option.icon!} url={option.logo?.url} />)}
                isSelected={option.id === selectedShippingMethod?.id}
                onClick={noop}
                title={option.title!}
              />
            ))}
            {!isFree && !!paymentStep?.payment?.id && !!paymentStep.payment && grandTotal && activeForm}
            <div id="adyen-payment-result-container" />
            {shippingLoading && paymentStep.payment?.type === 'credit_card' && <div style={{ height: '360px' }} />}
            <CheckoutTotals totals={totals || []} />
          </div>
          <CheckoutTermsOfService actionRef={termsRef} checked={!!paymentStep?.terms} onClick={value => updateStep('paymentStep', { terms: value })} />
        </CollapsableSection>
        <span className={styles.paymentButtonsWrapper}>
          {shippingLoading && <div className={clsx(styles.paymentLoaderWrapper, cofDesignV1 && styles.paymentLoaderWrapperCofDesignV1)} />}
          {!isFree && !isCODSelected && !!paymentStep?.payment?.id && grandTotal && activeButton}
          {isFree && (
            <Button
              key="free_payment"
              className={clsx(cofDesignV1 ? styles.buyButtonCofDesignV1 : styles.buyButton)}
              innerSpacing="medium"
              onClick={() => {
                updateStep('paymentStep', { payment: FREE_PAYMENT })
                setActiveStep(CheckoutFlowSteps.SUCCESS)
                submitCheckout(FREE_PAYMENT, finalizeCart => handlePaymentSubmitResponse(FREE_PAYMENT.type, finalizeCart))
                return
              }}
              theme={cofDesignV1 ? 'black' : 'primary'}
            >
              {t('checkout:COF_GO_TO_SECURE_PAYMENT')}
            </Button>
          )}
          {(!isFree && isCODSelected) && (
            <Button
              key="cod_payment"
              className={clsx(cofDesignV1 ? styles.buyButtonCofDesignV1 : styles.buyButton)}
              innerSpacing="medium"
              onClick={() => {
                updateStep('paymentStep', { payment: COD_PAYMENT })
                setActiveStep(CheckoutFlowSteps.SUCCESS)
                submitCheckout(COD_PAYMENT, response => handlePaymentSubmitResponse(COD_PAYMENT.type, response))
              }}
              theme={cofDesignV1 ? 'black' : 'primary'}
            >
              {t('checkout:COF_PLACE_ORDER_AND_PAY_ON_DELIVERY')}
            </Button>
          )}
        </span>
      </PaymentProviderBusProvider>
    </div>
  )
}
